import React, { ChangeEvent, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";
import { useListUsersQuery } from "../../../store/api/usersApi";

interface CreateLeaseData {
	propertyId: string;
	tenantId: string;
	startDate: string;
	endDate: string;
	rentAmount: string;
	securityDeposit?: string;
	terms?: string;
	documentFiles?: string[]; // Base64 encoded document files
}

interface CreateLeaseModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreateLease: (data: CreateLeaseData) => void;
}

interface LeaseFormData {
	tenantId: string;
	propertyId: string;
	unitId: string;
	startDate: string;
	endDate: string;
	rentAmount: string;
	rentPeriod: "annual" | "monthly";
	securityDeposit: string;
	leaseType: string;
	petPolicy: string;
	smokingPolicy: string;
	parkingSpaces: string;
	utilities: string[];
	documents: File[];
}

interface FormErrors {
	[key: string]: string;
}

const CreateLeaseModal: React.FC<CreateLeaseModalProps> = ({
	isOpen,
	onClose,
	onCreateLease,
}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [formData, setFormData] = useState<LeaseFormData>({
		// Step 1: Basic Information
		tenantId: "",
		propertyId: "",
		unitId: "",

		// Step 2: Lease Terms
		startDate: "",
		endDate: "",
		rentAmount: "",
		rentPeriod: "annual",
		securityDeposit: "",
		leaseType: "",

		// Step 3: Additional Terms
		petPolicy: "",
		smokingPolicy: "",
		parkingSpaces: "",
		utilities: [],

		// Step 4: Documents
		documents: [],
	});

	const [errors, setErrors] = useState<FormErrors>({});

	// Fetch real data from API
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 1000 });
	const { data: usersData } = useListUsersQuery({
		page_size: 1000,
		role: "tenant",
	});

	const tenantOptions =
		usersData?.results?.map((u) => ({
			value: u.id,
			label: `${u.first_name} ${u.last_name} (${u.email})`,
		})) || [];

	const propertyOptions =
		propertiesData?.results?.map((p) => ({
			value: p.id,
			label: `${p.name} - ${p.address}${
				p.monthly_rent ? ` (₦${p.monthly_rent.toLocaleString()}/month)` : ""
			}`,
		})) || [];

	// Note: Properties don't have units in the current model
	// Unit selection has been removed - lease code will be auto-generated

	const leaseTypeOptions = [
		{ value: "fixed", label: "Fixed Term" },
		{ value: "month-to-month", label: "Month-to-Month" },
		{ value: "renewable", label: "Auto-Renewable" },
	];

	const petPolicyOptions = [
		{ value: "no-pets", label: "No Pets Allowed" },
		{ value: "cats-only", label: "Cats Only" },
		{ value: "dogs-only", label: "Dogs Only" },
		{ value: "pets-allowed", label: "Pets Allowed" },
	];

	const smokingPolicyOptions = [
		{ value: "no-smoking", label: "No Smoking" },
		{ value: "designated-areas", label: "Designated Areas Only" },
		{ value: "smoking-allowed", label: "Smoking Allowed" },
	];

	const utilityOptions = [
		{ value: "electricity", label: "Electricity" },
		{ value: "gas", label: "Gas" },
		{ value: "water", label: "Water" },
		{ value: "internet", label: "Internet" },
		{ value: "cable", label: "Cable TV" },
		{ value: "trash", label: "Trash Collection" },
	];

	const handleInputChange = (
		field: keyof LeaseFormData,
		value: string | string[],
	) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const validateStep = (step: number): boolean => {
		const newErrors: FormErrors = {};

		switch (step) {
			case 1:
				if (!formData.tenantId) newErrors.tenantId = "Please select a tenant";
				if (!formData.propertyId)
					newErrors.propertyId = "Please select a property";
				// unitId is optional - removed validation
				break;

			case 2:
				if (!formData.startDate) newErrors.startDate = "Start date is required";
				if (!formData.endDate) newErrors.endDate = "End date is required";
				if (!formData.rentAmount)
					newErrors.rentAmount = "Rent amount is required";
				// securityDeposit is optional
				// leaseType is optional - removed validation

				// Validate date range
				if (formData.startDate && formData.endDate) {
					const start = new Date(formData.startDate);
					const end = new Date(formData.endDate);
					if (end <= start) {
						newErrors.endDate = "End date must be after start date";
					}
				}
				break;

			case 3:
				if (!formData.petPolicy) newErrors.petPolicy = "Pet policy is required";
				if (!formData.smokingPolicy)
					newErrors.smokingPolicy = "Smoking policy is required";
				break;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleNext = () => {
		if (validateStep(currentStep)) {
			setCurrentStep((prev) => prev + 1);
		}
	};

	const handlePrevious = () => {
		setCurrentStep((prev) => prev - 1);
	};

	// Compress image documents before converting to base64
	const compressDocument = (
		file: File,
		maxWidth = 1920,
		quality = 0.8,
	): Promise<string> => {
		// Only compress images, not PDFs or other documents
		// Validate file type - allow jpg, jpeg, png for images
		const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png"];
		const allowedImageExtensions = [".jpg", ".jpeg", ".png"];
		const isValidImageType = allowedImageTypes.includes(
			file.type?.toLowerCase(),
		);
		const isValidImageExtension = allowedImageExtensions.some((ext) =>
			file.name?.toLowerCase().endsWith(ext),
		);

		if (!isValidImageType && !isValidImageExtension) {
			return new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});
		}

		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const img = new Image();
				img.onload = () => {
					const canvas = document.createElement("canvas");
					let width = img.width;
					let height = img.height;

					// Resize if larger than maxWidth
					if (width > maxWidth) {
						height = (height * maxWidth) / width;
						width = maxWidth;
					}

					canvas.width = width;
					canvas.height = height;

					const ctx = canvas.getContext("2d");
					if (!ctx) {
						reject(new Error("Could not get canvas context"));
						return;
					}

					ctx.drawImage(img, 0, 0, width, height);
					const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
					resolve(compressedBase64);
				};
				img.onerror = reject;
				img.src = e.target?.result as string;
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const handleSubmit = async () => {
		if (validateStep(currentStep)) {
			try {
				// Convert document files to base64 with compression for images
				const documentFiles: string[] = [];

				// Process all documents in parallel
				const documentPromises = formData.documents.map(async (file) => {
					try {
						// Compress images, keep PDFs and other documents as-is
						return await compressDocument(file);
					} catch (error) {
						console.error("Error processing document:", error);
						// Fallback to original if compression fails
						return new Promise<string>((resolve, reject) => {
							const reader = new FileReader();
							reader.onload = () => resolve(reader.result as string);
							reader.onerror = reject;
							reader.readAsDataURL(file);
						});
					}
				});

				// Wait for all documents to be processed
				const processedDocuments = await Promise.all(documentPromises);
				documentFiles.push(...processedDocuments);

				// Convert rent amount to monthly (backend expects monthly_rent)
				const rentAmount = Number(formData.rentAmount) || 0;
				const monthlyRent =
					formData.rentPeriod === "annual"
						? rentAmount / 12 // Convert annual to monthly
						: rentAmount; // Already monthly

				// Transform form data to match API format
				const leaseData = {
					propertyId: formData.propertyId,
					tenantId: formData.tenantId,
					startDate: formData.startDate,
					endDate: formData.endDate,
					rentAmount: monthlyRent.toString(), // Always send as monthly to backend
					securityDeposit: formData.securityDeposit || undefined,
					terms:
						formData.petPolicy ||
						formData.smokingPolicy ||
						formData.parkingSpaces
							? `Pet Policy: ${formData.petPolicy || "N/A"}\nSmoking Policy: ${
									formData.smokingPolicy || "N/A"
							  }\nParking Spaces: ${
									formData.parkingSpaces || "N/A"
							  }\nUtilities: ${formData.utilities.join(", ") || "N/A"}`
							: undefined,
					documentFiles: documentFiles.length > 0 ? documentFiles : undefined,
				};
				await onCreateLease(leaseData);
				onClose();
				// Reset form
				setFormData({
					tenantId: "",
					propertyId: "",
					unitId: "",
					startDate: "",
					endDate: "",
					rentAmount: "",
					rentPeriod: "annual",
					securityDeposit: "",
					leaseType: "",
					petPolicy: "",
					smokingPolicy: "",
					parkingSpaces: "",
					utilities: [],
					documents: [],
				});
				setCurrentStep(1);
				setErrors({});
			} catch (error) {
				console.error("Error creating lease:", error);
				// Errors will be handled by parent component
			}
		}
	};

	const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		setFormData((prev) => ({
			...prev,
			documents: [...prev.documents, ...files],
		}));
	};

	const removeDocument = (index: number) => {
		setFormData((prev) => ({
			...prev,
			documents: prev.documents.filter((_, i) => i !== index),
		}));
	};

	if (!isOpen) return null;

	const steps = [
		{
			number: 1,
			title: "Basic Information",
			description: "Select tenant and property",
		},
		{ number: 2, title: "Lease Terms", description: "Set dates and amounts" },
		{
			number: 3,
			title: "Additional Terms",
			description: "Policies and utilities",
		},
		{ number: 4, title: "Documents", description: "Upload lease documents" },
	];

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-0 sm:p-4">
			<div className="bg-card border-0 sm:border sm:border-border rounded-none sm:rounded-lg w-full max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden modal-shadow">
				{/* Header */}
				<div className="p-4 sm:p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div className="flex-1 min-w-0 mr-2">
							<h2 className="text-lg sm:text-xl font-semibold text-foreground">
								Create New Lease
							</h2>
							<p className="text-xs sm:text-sm text-muted-foreground mt-1">
								Step {currentStep} of {steps.length}:{" "}
								{steps[currentStep - 1].description}
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							iconName="X"
						/>
					</div>

					{/* Progress Steps */}
					<div className="flex items-center justify-between mt-4 sm:mt-6">
						{steps.map((step, index) => (
							<div key={step.number} className="flex items-center">
								<div
									className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-medium ${
										currentStep >= step.number
											? "bg-primary text-primary-foreground"
											: "bg-muted text-muted-foreground"
									}`}
								>
									{currentStep > step.number ? (
										<Icon name="Check" size={14} />
									) : (
										step.number
									)}
								</div>
								{index < steps.length - 1 && (
									<div
										className={`w-6 sm:w-12 h-0.5 mx-1 sm:mx-2 ${
											currentStep > step.number ? "bg-primary" : "bg-muted"
										}`}
									/>
								)}
							</div>
						))}
					</div>
				</div>

				{/* Content */}
				<div
					className="p-4 sm:p-6 overflow-y-auto"
					style={{ maxHeight: "calc(100vh - 240px)" }}
				>
					{/* Step 1: Basic Information */}
					{currentStep === 1 && (
						<div className="space-y-6">
							<Select
								label="Select Tenant"
								options={tenantOptions}
								value={formData.tenantId}
								onValueChange={(value) =>
									handleInputChange("tenantId", String(value))
								}
								error={errors.tenantId}
								required
								searchable
							/>

							<Select
								label="Select Property"
								options={propertyOptions}
								value={formData.propertyId}
								onValueChange={(value) => {
									const propertyId = String(value);
									handleInputChange("propertyId", propertyId);
									// Auto-fill rent amount and period if property is selected
									if (propertyId) {
										const selectedProperty = propertiesData?.results?.find(
											(p) => p.id === propertyId,
										);
										if (selectedProperty) {
											// Use rent_amount and rent_type if available, otherwise fall back to monthly_rent
											if (
												selectedProperty.rent_amount &&
												selectedProperty.rent_type
											) {
												handleInputChange(
													"rentAmount",
													selectedProperty.rent_amount.toString(),
												);
												handleInputChange(
													"rentPeriod",
													selectedProperty.rent_type === "annual"
														? "annual"
														: "monthly",
												);
											} else if (selectedProperty.monthly_rent) {
												handleInputChange(
													"rentAmount",
													selectedProperty.monthly_rent.toString(),
												);
												handleInputChange("rentPeriod", "monthly");
											}
										}
									} else {
										// Clear rent if no property selected
										handleInputChange("rentAmount", "");
									}
								}}
								error={errors.propertyId}
								required
								searchable
							/>

							{/* Note: Unit selection removed - properties don't have units in current model */}
							{/* Lease code will be auto-generated by backend when lease is created */}
							<div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
								<div className="flex items-start space-x-3">
									<Icon name="Info" size={16} className="text-primary mt-0.5" />
									<div>
										<p className="text-sm font-medium text-foreground">
											Lease Code
										</p>
										<p className="text-xs text-muted-foreground mt-1">
											A unique lease code (e.g., LC-2025-0001) will be
											automatically generated when the lease is created.
										</p>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Step 2: Lease Terms */}
					{currentStep === 2 && (
						<div className="space-y-6">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<Input
									label="Start Date"
									type="date"
									value={formData.startDate}
									onChange={(e) =>
										handleInputChange("startDate", e.target.value)
									}
									error={errors.startDate}
									required
								/>

								<Input
									label="End Date"
									type="date"
									value={formData.endDate}
									onChange={(e) => handleInputChange("endDate", e.target.value)}
									error={errors.endDate}
									required
								/>
							</div>

							<div className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<Select
											label="Rent Period"
											options={[
												{ value: "annual", label: "Annual" },
												{ value: "monthly", label: "Monthly" },
											]}
											value={formData.rentPeriod}
											onValueChange={(value) =>
												handleInputChange(
													"rentPeriod",
													value as "annual" | "monthly",
												)
											}
											description="Select whether rent is paid annually or monthly"
										/>
									</div>
									<Input
										label={`Rent Amount (${
											formData.rentPeriod === "annual" ? "Annual" : "Monthly"
										})`}
										type="number"
										placeholder={
											formData.rentPeriod === "annual" ? "30000" : "2500"
										}
										value={formData.rentAmount}
										onChange={(e) =>
											handleInputChange("rentAmount", e.target.value)
										}
										error={errors.rentAmount}
										required
									/>
								</div>
								<Input
									label="Security Deposit"
									type="number"
									placeholder="0"
									value={formData.securityDeposit}
									onChange={(e) =>
										handleInputChange("securityDeposit", e.target.value)
									}
									error={errors.securityDeposit}
									description="Optional"
								/>
							</div>

							<Select
								label="Lease Type"
								options={leaseTypeOptions}
								value={formData.leaseType}
								onValueChange={(value) =>
									handleInputChange("leaseType", String(value))
								}
								error={errors.leaseType}
								description="Optional - for reference only"
							/>
						</div>
					)}

					{/* Step 3: Additional Terms */}
					{currentStep === 3 && (
						<div className="space-y-6">
							<Select
								label="Pet Policy"
								options={petPolicyOptions}
								value={formData.petPolicy}
								onValueChange={(value) =>
									handleInputChange("petPolicy", String(value))
								}
								error={errors.petPolicy}
								required
							/>

							<Select
								label="Smoking Policy"
								options={smokingPolicyOptions}
								value={formData.smokingPolicy}
								onValueChange={(value) =>
									handleInputChange("smokingPolicy", String(value))
								}
								error={errors.smokingPolicy}
								required
							/>

							<Input
								label="Parking Spaces"
								type="number"
								placeholder="1"
								value={formData.parkingSpaces}
								onChange={(e) =>
									handleInputChange("parkingSpaces", e.target.value)
								}
								description="Number of assigned parking spaces"
							/>

							<Select
								label="Utilities Included"
								options={utilityOptions}
								value={formData.utilities}
								onValueChange={(value) =>
									handleInputChange(
										"utilities",
										Array.isArray(value) ? value.map(String) : [String(value)],
									)
								}
								multiple
								description="Select all utilities included in rent"
							/>
						</div>
					)}

					{/* Step 4: Documents */}
					{currentStep === 4 && (
						<div className="space-y-6">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Upload Lease Documents
								</label>
								<div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
									<Icon
										name="Upload"
										size={48}
										className="mx-auto text-muted-foreground mb-4"
									/>
									<p className="text-muted-foreground mb-4">
										Drag and drop files here, or click to select
									</p>
									<input
										type="file"
										multiple
										accept=".pdf,.doc,.docx"
										onChange={handleFileUpload}
										className="hidden"
										id="document-upload"
									/>
									<Button
										variant="outline"
										onClick={() => {
											const uploadInput = document.getElementById(
												"document-upload",
											) as HTMLInputElement;
											uploadInput?.click();
										}}
										iconName="Plus"
										iconPosition="left"
									>
										Select Files
									</Button>
								</div>
							</div>

							{/* Uploaded Documents */}
							{formData.documents.length > 0 && (
								<div>
									<h4 className="font-medium text-foreground mb-3">
										Uploaded Documents
									</h4>
									<div className="space-y-2">
										{formData.documents.map((doc: File, index: number) => (
											<div
												key={index}
												className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
											>
												<div className="flex items-center space-x-3">
													<Icon
														name="FileText"
														size={20}
														className="text-muted-foreground"
													/>
													<span className="text-sm text-foreground">
														{doc.name}
													</span>
												</div>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => removeDocument(index)}
													iconName="X"
													className="text-error hover:text-error"
												/>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="p-4 sm:p-6 border-t border-border">
					<div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
						<Button
							variant="ghost"
							onClick={handlePrevious}
							disabled={currentStep === 1}
							iconName="ChevronLeft"
							iconPosition="left"
							className="w-full sm:w-auto order-2 sm:order-1"
						>
							Previous
						</Button>

						<div className="flex items-center gap-2 sm:gap-3 order-1 sm:order-2">
							<Button
								variant="ghost"
								onClick={onClose}
								className="flex-1 sm:flex-initial"
							>
								Cancel
							</Button>

							{currentStep < steps.length ? (
								<Button
									onClick={handleNext}
									iconName="ChevronRight"
									iconPosition="right"
									className="flex-1 sm:flex-initial"
								>
									Next
								</Button>
							) : (
								<Button
									onClick={handleSubmit}
									iconName="Check"
									iconPosition="left"
									className="flex-1 sm:flex-initial"
								>
									Create Lease
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CreateLeaseModal;
